// Score history: runs grouped into series that can share an axis, and one point per run.
//
// Only runs on the same dataset, execution mode, model and baseline pipeline are
// comparable; mixing a 4-call simulated dataset with a 10-call real one, or the 5-stage
// pipeline with the single prompt, on one line invents a trend. Each run contributes its
// BASELINE (normally the live prompt) with a 95% range, and the candidate it tested.
// A point can show the overall score, one stage or field, cost per call, or latency.

import { analyzeModel } from './analysis'
import { isNoiseCheck, noiseMarginFor, runLabels } from './runMeta'
import { accuracyRange, comparePaired, meanRange, type Tally, type Verdict } from './stats'
import type { ExtractionScore, Stage } from './scoring'
import type { ExecutionMode, ItemResult, PromptSet, Run } from './types'

export type Pipeline = 'staged' | 'single'

export const PIPELINE_LABEL: Record<Pipeline, string> = { staged: `5 stages`, single: `Single prompt` }

export const pipelineOf = (set: PromptSet): Pipeline => (set.single === undefined ? `staged` : `single`)

export type TrendSeries = {
  key: string
  datasetId: string
  datasetName: string
  execution: ExecutionMode
  model: string
  /** The pipeline the baseline runs: the line on the chart. */
  pipeline: Pipeline
  runs: Run[]
}

export type Metric = 'score' | 'cost' | 'latency'

/** What a score point measures: the overall score, one stage, or one field of a stage. */
export type Focus = { stage?: Stage; field?: string }

export type TrendPoint = {
  run: Run
  date: Date
  title: string
  baselineLabel: string
  candidateLabel: string
  baseline: number
  baselineRange: { low: number; high: number }
  candidate: number
  /** The run's verdict on this focus; null for cost and latency, which get no verdict. */
  verdict: Verdict | null
  noiseCheck: boolean
  /** The candidate ran the other pipeline (e.g. the single prompt against the stages). */
  crossPipeline: boolean
  items: number
}

/** Comparable groups of runs, most runs first; real runs win ties. */
export function trendSeries(runs: Run[]): TrendSeries[] {
  const groups = new Map<string, TrendSeries>()
  for (const run of runs) {
    if (run.status !== `complete`) continue
    const pipeline = pipelineOf(run.baseline)
    for (const result of run.modelResults) {
      const key = `${run.datasetId}|${run.execution}|${result.model}|${pipeline}`
      const group = groups.get(key) ?? {
        key,
        datasetId: run.datasetId,
        datasetName: run.datasetName,
        execution: run.execution,
        model: result.model,
        pipeline,
        runs: [],
      }
      group.runs.push(run)
      groups.set(key, group)
    }
  }
  return [...groups.values()].sort(
    (a, b) =>
      b.runs.length - a.runs.length || Number(b.execution !== `simulated`) - Number(a.execution !== `simulated`),
  )
}

export type SeriesFilter = { datasetId?: string; execution?: ExecutionMode; model?: string; pipeline?: Pipeline }

export type SeriesOptions = {
  datasetId: string[]
  execution: ExecutionMode[]
  model: string[]
  pipeline: Pipeline[]
}

const FILTER_DIMENSIONS = [`datasetId`, `execution`, `model`, `pipeline`] as const

/**
 * Narrows the series one dimension at a time (dataset, then mode, model, pipeline). Each
 * dimension offers only the values left by the choices before it, and a choice that is
 * no longer offered falls back to the first, so the pickers never reach an empty chart.
 */
export function pickSeries(
  series: TrendSeries[],
  filter: SeriesFilter,
): { selected: TrendSeries | undefined; options: SeriesOptions } {
  let remaining = series
  const options: Record<string, string[]> = {}
  for (const dimension of FILTER_DIMENSIONS) {
    const values: string[] = [...new Set(remaining.map((entry) => entry[dimension]))]
    options[dimension] = values
    const wanted = filter[dimension]
    const value = wanted !== undefined && values.includes(wanted) ? wanted : values[0]
    remaining = remaining.filter((entry) => entry[dimension] === value)
  }
  return { selected: remaining[0], options: options as SeriesOptions }
}

/** The stages and fields a series' runs scored, for the focus picker. */
export function focusOptions(series: TrendSeries): { stage: Stage; fields: string[] }[] {
  const byStage = new Map<Stage, Set<string>>()
  for (const run of series.runs) {
    for (const stage of run.stages) byStage.set(stage, byStage.get(stage) ?? new Set())
    const result = run.modelResults.find((entry) => entry.model === series.model)
    for (const field of Object.values(result?.fieldAccuracy ?? {})) byStage.get(field.stage)?.add(field.field)
  }
  return [...byStage].map(([stage, fields]) => ({ stage, fields: [...fields] }))
}

/** One item's score narrowed to the focus; a field an item does not have scores nothing. */
function focusTally(score: ExtractionScore, focus: Focus): Tally {
  if (!focus.stage) return score
  const fields = score.stages[focus.stage].fields.filter((field) => !focus.field || field.field === focus.field)
  const matched = fields.filter((field) => field.matched).length
  return { matched, total: fields.length, overall: fields.length ? matched / fields.length : 1 }
}

function pooled(tallies: Tally[]): number {
  const total = tallies.reduce((sum, tally) => sum + tally.total, 0)
  return total ? tallies.reduce((sum, tally) => sum + tally.matched, 0) / total : 1
}

/** Per-call cost (dollars) or latency (seconds) of one side of an item. */
const SIDE_VALUE: Record<Exclude<Metric, 'score'>, (side: { cost: number; latencyMs: number }) => number> = {
  cost: (side) => side.cost,
  latency: (side) => side.latencyMs / 1000,
}

export function trendPoints(
  series: TrendSeries,
  allRuns: Run[],
  metric: Metric = `score`,
  focus: Focus = {},
): TrendPoint[] {
  return series.runs
    .map((run): TrendPoint => {
      const result = run.modelResults.find((entry) => entry.model === series.model)!
      const labels = runLabels(run)
      const common = {
        run,
        date: new Date(run.startedAt),
        title: labels.title,
        baselineLabel: labels.baseline,
        candidateLabel: labels.candidate,
        noiseCheck: isNoiseCheck(run),
        crossPipeline: pipelineOf(run.candidate) !== series.pipeline,
        items: result.items.length,
      }
      if (metric !== `score`) {
        const value = SIDE_VALUE[metric]
        const baseline = meanRange(result.items.map((item) => value(item.baseline)))
        const candidate = meanRange(result.items.map((item: ItemResult) => value(item))).mean
        return { ...common, baseline: baseline.mean, baselineRange: baseline, candidate, verdict: null }
      }

      const margin = noiseMarginFor(run, series.model, allRuns).margin
      const pairs = result.items
        .map((item) => ({
          baseline: focusTally(item.baselineScore, focus),
          candidate: focusTally(item.candidateScore, focus),
        }))
        .filter((pair) => pair.baseline.total + pair.candidate.total > 0)
      const verdict = focus.stage
        ? comparePaired(pairs, `${run.id}:${focus.stage}.${focus.field ?? ``}`, margin).verdict
        : analyzeModel(result, run.stages, run.id, margin).comparison.verdict
      return {
        ...common,
        items: pairs.length,
        baseline: pooled(pairs.map((pair) => pair.baseline)),
        baselineRange: accuracyRange(
          pairs.map((pair) => pair.baseline),
          `${run.id}:${series.model}:baseline`,
        ),
        candidate: pooled(pairs.map((pair) => pair.candidate)),
        verdict,
      }
    })
    .sort((a, b) => a.date.getTime() - b.date.getTime())
}

/**
 * One honest sentence about the baseline's movement across the series: a change counts
 * only when the first and last 95% ranges do not overlap.
 */
export function trendSummary(points: TrendPoint[], format: (value: number) => string): string | null {
  if (points.length < 2) return null
  const first = points[0]
  const last = points[points.length - 1]
  const moved =
    last.baselineRange.low > first.baselineRange.high
      ? `up`
      : last.baselineRange.high < first.baselineRange.low
        ? `down`
        : null
  const span = `from ${format(first.baseline)} to ${format(last.baseline)} across ${points.length} runs`
  return moved
    ? `The baseline moved ${moved} ${span}, beyond the overlap of its 95% ranges.`
    : `The baseline went ${span}, but its first and last 95% ranges overlap: no measurable change yet.`
}
